import { useCallback, useEffect, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { emailRegexp, taxCodeRegexp } from '../../helpers/regexp';
import { useLanguage } from '../../locale/useLanguage';
import { GetGeoInfoParams } from '../../models/address.models';
import { Profile } from '../../models/user.models';

export type CompleteBusinessProfileForm = {
  companyName: string;
  vatNumber: string;
  fiscalCode: string;
  legalRepresentative: string;
  companyAddress: string;
  companyPostcode?: string;
  companyMunicipality?: string;
  companyProvince?: string;
  companyCountryCode: string;
  companySDICode: string;
  companyPEC: string;
};

export const useCompleteUserInfoBusinessValidationForm = (
  profile?: Profile,
  geoInfoUpdater?: ({
    country,
    state,
    city,
    postalCode,
  }: GetGeoInfoParams) => void
) => {
  const [t] = useLanguage();
  const [fiscalValue, setFiscalValue] = useState();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    setError,
    getValues,
    formState: { errors },
    watch,
  } = useForm<CompleteBusinessProfileForm>();

  const watchCompanyCountryCode = watch('companyCountryCode', undefined);
  const watchCompanyState = watch('companyProvince', undefined);
  const watchCompanyCity = watch('companyMunicipality', undefined);

  const getCorrectCountryInfo = useCallback(async () => {
    if (
      typeof geoInfoUpdater === 'function' &&
      profile &&
      profile.companyCountryCode
    ) {
      await geoInfoUpdater({ country: profile.companyCountryCode, state: '' });

      if (profile.companyProvince) {
        await geoInfoUpdater({
          country: profile.companyCountryCode,
          state: profile.companyProvince,
          city: '',
        });

        if (profile.companyMunicipality) {
          await geoInfoUpdater({
            country: profile.companyCountryCode,
            state: profile.companyProvince,
            city: profile.companyMunicipality,
            postalCode: '',
          });
        }
      }
    }
  }, [geoInfoUpdater, profile]);

  useEffect(() => {
    if (profile) {
      const getCorrectCountryInfoAsync = async () => {
        if (typeof geoInfoUpdater === 'function') {
          await getCorrectCountryInfo();
        }
        profile.companyAddress &&
          setValue('companyAddress', profile.companyAddress);
        profile.companyName && setValue('companyName', profile.companyName);
        profile.fiscalCode && setValue('fiscalCode', profile.fiscalCode);
        profile.companySDICode &&
          setValue('companySDICode', profile.companySDICode);
        profile.companyPEC && setValue('companyPEC', profile.companyPEC);
        profile.vatNumber && setValue('vatNumber', profile.vatNumber);
        profile.legalRepresentative &&
          setValue('legalRepresentative', profile.legalRepresentative);
        profile?.companyCountryCode &&
          setValue('companyCountryCode', profile?.companyCountryCode);
        profile.companyProvince &&
          setValue('companyProvince', profile.companyProvince);
        profile.companyMunicipality &&
          setValue('companyMunicipality', profile.companyMunicipality);
        profile.companyPostcode &&
          setValue('companyPostcode', profile.companyPostcode);
      };
      getCorrectCountryInfoAsync();
    }
  }, [geoInfoUpdater, getCorrectCountryInfo, profile, setValue]);

  useEffect(() => {
    if (watchCompanyCountryCode !== 'IT')
      setValue('companyProvince', undefined);
  }, [watchCompanyCountryCode, setValue]);

  const companyNameValidator = useMemo(
    () => ({
      ...register('companyName', {
        required: t('completeUserInfoView.companyNameError'),
      }),
    }),
    [register, t]
  );

  const vatNumberValidator = useMemo(
    () => ({
      ...register('vatNumber', {
        required: t('completeUserInfoView.vatNumberError'),
      }),
    }),
    [register, t]
  );

  const postcodeValidator = useMemo(
    () => ({
      ...register('companyPostcode', {
        required: t('completeUserInfoView.requiredField'),
        disabled: !watchCompanyCity,
      }),
    }),
    [register, t, watchCompanyCity]
  );

  const {
    field: companyMunicipalityField,
    fieldState: companyMunicipalityFieldState,
  } = useController<CompleteBusinessProfileForm, 'companyMunicipality'>({
    name: 'companyMunicipality',
    control,
    rules: { required: t('completeUserInfoView.requiredField') },
  });

  useEffect(() => {
    if (profile && !companyMunicipalityFieldState.isDirty) {
      profile.companyMunicipality &&
        companyMunicipalityField.onChange(profile.companyMunicipality);
    }
  }, [companyMunicipalityField, companyMunicipalityFieldState, profile]);

  const companyMunicipalityValidator = useMemo(
    () => ({
      ...companyMunicipalityField,
      onChange: (event: any) => {
        typeof geoInfoUpdater === 'function' &&
          watchCompanyCountryCode === 'IT' &&
          geoInfoUpdater({
            country: watchCompanyCountryCode,
            state: watchCompanyState,
            city: event.target.value,
            postalCode: '',
          });
        setValue('companyPostcode', undefined);
        companyMunicipalityField.onChange(event.target.value);
      },
      control: control,
      disabled: !watchCompanyState || !watchCompanyCountryCode,
    }),

    [
      companyMunicipalityField,
      control,
      geoInfoUpdater,
      setValue,
      watchCompanyCountryCode,
      watchCompanyState,
    ]
  );

  const companyProvinceValidator = useMemo(
    () => ({
      ...register('companyProvince', {
        required:
          watchCompanyCountryCode === 'IT'
            ? t('completeUserInfoView.requiredField')
            : false,
        disabled: !watchCompanyCountryCode,
        onChange: ({ target: { value } }) => {
          typeof geoInfoUpdater === 'function' &&
            geoInfoUpdater({
              country: watchCompanyCountryCode,
              state: value,
              city: '',
            });
          setValue('companyProvince', value);
          setValue('companyMunicipality', undefined);
          setValue('companyPostcode', undefined);
        },
      }),
    }),
    [geoInfoUpdater, register, setValue, t, watchCompanyCountryCode]
  );

  const companySDICodeValidator = useMemo(
    () => ({
      ...register('companySDICode', {
        required: t('completeUserInfoView.requiredField'),
      }),
    }),
    [register, t]
  );

  const companyCountryCodeValidator = useMemo(
    () => ({
      ...register('companyCountryCode', {
        required: t('completeUserInfoView.requiredField'),
        onChange: ({ target: { value } }) => {
          typeof geoInfoUpdater === 'function' &&
            geoInfoUpdater({ country: value, state: '' });
          setValue('companyCountryCode', value);
          setValue('companyProvince', undefined);
          setValue('companyMunicipality', undefined);
          setError('companyMunicipality', {});
          setValue('companyPostcode', undefined);
          setError('companyPostcode', {});
        },
      }),
    }),
    [geoInfoUpdater, register, setError, setValue, t]
  );

  const companyPECValidator = useMemo(
    () => ({
      ...register('companyPEC', {
        required: t('completeUserInfoView.requiredField'),
        pattern: {
          value: emailRegexp,
          message: t('signInView.emailError'),
        },
      }),
    }),
    [register, t]
  );

  const taxCodeValidator = useMemo(
    () => ({
      ...register('fiscalCode', {
        required: t('completeUserInfoView.taxCodeRequiredError'),
        pattern: {
          value: taxCodeRegexp,
          message: t('completeUserInfoView.taxCodeNotValid'),
        },
        value: fiscalValue,
        onChange: ({ target: { value } }) => {
          setFiscalValue(value.toUpperCase());
          setValue('fiscalCode', value.toUpperCase());
        },
      }),
    }),
    [fiscalValue, register, setValue, t]
  );

  const legalRepresentativeValidator = useMemo(
    () => ({
      ...register('legalRepresentative', {
        required: t('completeUserInfoView.legalRepresentativeError'),
      }),
    }),
    [register, t]
  );

  const legalAddressValidator = useMemo(
    () => ({
      ...register('companyAddress', {
        required: t('completeUserInfoView.legalAddressError'),
      }),
    }),
    [register, t]
  );

  return {
    watchCompanyCountryCode,
    companyNameValidator,
    postcodeValidator,
    companyMunicipalityValidator,
    companyProvinceValidator,
    companySDICodeValidator,
    companyPECValidator,
    companyCountryCodeValidator,
    legalAddressValidator,
    legalRepresentativeValidator,
    companyMunicipalityFieldState,
    companyMunicipalityField,
    taxCodeValidator,
    vatNumberValidator,
    handleSubmit,
    getValues,
    setValue,
    errors,
  };
};
